import request from "../../utils/request";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { trackHostLoginSuccess } from "../../utils/hostAnalytics";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid.trim();
    if (typeof value._id === "string") return value._id.trim();
    if (typeof value.id === "string") return value.id.trim();
  }
  return "";
};

const normalizeOrdersFromResponse = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.orders)) return responseData.orders;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.result)) return responseData.result;
  return [];
};

const normalizePropertiesFromResponse = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.properties)) return responseData.properties;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.result)) return responseData.result;
  return [];
};

const getOrderPropertyId = (order = {}) =>
  normalizeId(order?.propertyID || order?.propertyId || order?.property?._id || order?.property?.id);

const getOrderHostIdCandidates = (order = {}) =>
  [
    order?.partnerHostID,
    order?.partnerHostId,
    order?.hostID,
    order?.hostId,
    order?.host?._id,
    order?.host?.id,
    order?.property?.hostID,
    order?.property?.hostId,
    order?.property?.host?._id,
    order?.property?.host?.id,
  ]
    .map(normalizeId)
    .filter(Boolean);

const getHostPropertyIdSet = (propertiesPayload, hid) => {
  const hostId = normalizeId(hid);
  const ids = new Set();

  normalizePropertiesFromResponse(propertiesPayload).forEach((property) => {
    const propertyId = normalizeId(property?._id || property?.id);
    const propertyHostId = normalizeId(
      property?.hostID || property?.hostId || property?.host?._id || property?.host
    );

    if (!propertyId) return;
    if (hostId && propertyHostId && propertyHostId !== hostId) return;
    ids.add(propertyId);
  });

  return ids;
};

const mergeAndFilterHostOrders = (responses = [], hostPropertyIds = new Set(), hid = "") => {
  const normalizedHostId = normalizeId(hid);
  const normalizedHostPropertyIds = new Set(Array.from(hostPropertyIds).map(normalizeId).filter(Boolean));
  const mergedById = new Map();

  responses.forEach((responseData) => {
    normalizeOrdersFromResponse(responseData).forEach((order) => {
      const key = normalizeId(order?._id) || normalizeId(order?.uniqueId);
      if (!key) return;

      if (normalizedHostPropertyIds.size > 0) {
        const orderPropertyId = getOrderPropertyId(order);
        const orderHostIds = getOrderHostIdCandidates(order);
        const matchesProperty = orderPropertyId && normalizedHostPropertyIds.has(orderPropertyId);

        // Keep host-linked records even when propertyID is absent/variant.
        const matchesHost =
          normalizedHostId && orderHostIds.length > 0
            ? orderHostIds.includes(normalizedHostId)
            : false;

        if (!matchesProperty && !matchesHost) return;
      }
      mergedById.set(key, order);
    });
  });

  return Array.from(mergedById.values()).sort((a, b) => {
    const dateA = new Date(a?.inStockDate || a?.createdAt || 0).getTime();
    const dateB = new Date(b?.inStockDate || b?.createdAt || 0).getTime();
    return dateB - dateA;
  });
};

const hostLogin = createAsyncThunk(
  "hostLogin/hostlogin",
  async (loginCredentials, { rejectWithValue }) => {
    try {
      const { data } = await request.post("/hosts/login", loginCredentials, {
        withCredentials: true,
      });

      localStorage.setItem("hostInfo", JSON.stringify(data.user));

      trackHostLoginSuccess({
        hostId: data.user?._id,
        method: "password",
      });

      return data;
    } catch (err) {
      const status = err.response?.status;
      const responseData = err.response?.data;

      if (status === 429) {
        return rejectWithValue({ error: "Please try again in 15 mins." });
      }

      const errorMessage =
        (responseData && typeof responseData === "object" && (responseData.error || responseData.message)) ||
        "Wrong Email or Password";

      return rejectWithValue({ error: typeof errorMessage === "string" ? errorMessage : "Wrong Email or Password" });
    }
  }
);

const hostSignup = createAsyncThunk(
  "hostSignup/hostSignup",
  async (payload, { rejectWithValue }) => {
    const { captchaResponse, ...signUpCredentials } = payload;

    try {
      const requestBody = {
        ...signUpCredentials, // This now correctly represents the rest of the form data
        captchaResponse, // Assuming your backend expects this key for the captcha token
      };

      const { data } = await request.post("/hosts/register", requestBody, {
        withCredentials: true,
      });

      // Track only a confirmed account creation, not form submissions or errors.
      if (data?.success === true) {
        try {
          if (typeof window !== "undefined" && typeof window.oaiq === "function") {
            window.oaiq("measureSingle", "HutY3SCSJtVessAv4w6h7K", "registration_completed", {
              type: "customer_action",
            });
          }
        } catch (trackingError) {
          // Conversion measurement must never interrupt successful registration.
        }
      }

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);


const getHostProfile = createAsyncThunk(
  "hostProfile/gethostProfile",
  async (id, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await request.get(`/hosts/${id}`, {
        withCredentials: true,
      });
      data.profilePictureUrl = data.profilePicture;
      const state = getState();
      if(!state.hostDetails.Info){
        // const resp = await fetch(data.profilePicture);
        // const blob = await resp.blob();
        // data.profilePicture = URL.createObjectURL(blob);
        data.profilePicture = "";
        dispatch(getHostProfileImage(data.profilePictureUrl));
      }else {
        data.profilePicture = state.hostDetails.Info.profilePicture;
      }
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: err.message || "Failed to fetch host profile" });
    }
  }
);

const getHostProfileImage = createAsyncThunk(
  "hostProfile/gethostProfileImage",
  async (profilePictureUrl, { rejectWithValue, getState }) => {
    console.log("Called");
    try {
      const state = getState();
      const resp = await fetch(profilePictureUrl ? profilePictureUrl : state.hostDetails.Info.profilePictureUrl);
      const blob = await resp.blob();
      const profilePicture = URL.createObjectURL(blob);
      console.log(profilePicture)
      return profilePicture;
    } catch (err) {
      console.log(err);
      return rejectWithValue(err.response.data);
    }
  }
);




const updateHostProfile = createAsyncThunk(
  "hostUpdate/updateHostProfile",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await request.patch(
        `/hosts/${credentials.hid}`,
        credentials.profileCredentials,
        { withCredentials: true }
      );

      console.log("data", data);

      // localStorage.setItem("hostInfo", JSON.stringify(data.hostDetails));

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const hostChangePassword = createAsyncThunk(
  "hostChangePassword/hostChangePassword",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await request.patch(
        `/hosts/change-password/${credentials.hid}`,
        credentials.passwordCredentials,
        { withCredentials: true }
      );

      return data;
    }catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const hostChangeEmail = createAsyncThunk(
  "hostChangeEmail/hostChangeEmail",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await request.patch(
        `/hosts/update-email/${credentials.hid}`,
        credentials.emailCredentials,
        { withCredentials: true }
      );

      return data;
    }catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const getHostProperties = createAsyncThunk(
  "hostProperties/getHostProperties",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`/properties/host/${id}`, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const updateHostProperty = createAsyncThunk(
  "hostProperties/updateHostProperty",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await request.patch(
        `/properties/${credentials.pid}`,
        credentials.propertyCredentials,
        { withCredentials: true }
      );

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const getPropertyDetails = createAsyncThunk(
  "propertyDetails/getPropertyDetails",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`/properties/${id}`, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const deleteHostProperty = createAsyncThunk(
  "deleteProperty/deletehostProperty",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await request.delete(`/properties/${id}`, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const addProperty = createAsyncThunk(
  "addHostProperty/addProperty",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await request.post(`/properties`, credentials, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const hostResetPassword = createAsyncThunk(
  "hostResetPassword/hostResetPassword",
  async (credentials, { rejectWithValue }) => {
    const passwordResetToken = credentials.passwordResetToken;
    const newPassword = credentials.newPassword;
    try {
      const { data } = await request.put(
        `/hosts/resetpassword/${passwordResetToken}`,
        { newPassword },
        {
          withCredentials: true,
        }
      );

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

//Implement Get All Property Orders
const getHostEarnings = createAsyncThunk(
  "hostEarnings/getHostEarnings",
  async (hid, { rejectWithValue }) => {
    try {
      const { data } = await request.get(
        `/orders/graph/${hid}?type=host&status=completed`,
        {
          withCredentials: true,
        }
      );

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const getHostPendingEarnings = createAsyncThunk(
  "hostPendingEarnings/getHostPendingEarnings",
  async (hid, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`/orders/host/${hid}?status=pending`, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const getHostAccountBalance = createAsyncThunk(
  "hostAccountBalance/getHostAccountBalance",
  async (hid, { rejectWithValue }) => {
    try {
      const { data } = await request.get(`/hosts/account-balance/${hid}`, {
        withCredentials: true,
      });

      return data.available[0].amount;
    } catch (err) {
      console.log("thunk error", err);
      return rejectWithValue(err.response.data);
    }
  }
);

const getAllOrders = createAsyncThunk(
  "orders/getAllOrders",
  async (hid, { rejectWithValue }) => {
    try {
      // Host controller defaults omit paymentCompleted unless status is explicitly provided.
      const statusParam = [
        "requested",
        "pending",
        "paymentCompleted",
        "picked",
        "completed",
        "cancelled",
      ].join(",");

      const { data } = await request.get(
        `/orders/host/${hid}?status=${encodeURIComponent(statusParam)}&_=${Date.now()}`,
        {
          withCredentials: true,
        }
      );

      const orders = mergeAndFilterHostOrders([data], new Set(), hid);

      return {
        success: true,
        orders,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: err.message || "Failed to fetch host orders" });
    }
  }
);

const getFlags = createAsyncThunk(
  "hostFlags/getFlags",
  async (hid, { rejectWithValue }) => {
    console.log("called", hid);
    try {
      const { data } = await request.get(`/hosts/automation/${hid}`, {
        withCredentials: true,
      });

      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

/**
 * Update host's entry instruction email preferences.
 * Body: { receiveEntryInstructionsEmails: boolean, entryInstructionsAdditionalEmails?: string[] }
 * Backend must validate: authenticated host can only update own prefs; validate email format; enforce max list size.
 */
const updateEntryInstructionEmailPrefs = createAsyncThunk(
  "hostFlags/updateEntryInstructionEmailPrefs",
  async ({ hid, receiveEntryInstructionsEmails, entryInstructionsAdditionalEmails }, { rejectWithValue }) => {
    try {
      const { data } = await request.patch(
        `/hosts/entry-instruction-emails/${hid}`,
        { receiveEntryInstructionsEmails, entryInstructionsAdditionalEmails },
        { withCredentials: true }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err);
    }
  }
);

export {
  hostLogin,
  hostSignup,
  getHostProperties,
  updateHostProperty,
  getPropertyDetails,
  deleteHostProperty,
  getHostProfile,
  updateHostProfile,
  addProperty,
  hostResetPassword,
  getHostEarnings,
  getHostPendingEarnings,
  getHostAccountBalance,
  getAllOrders,
  hostChangeEmail,
  hostChangePassword,
  getHostProfileImage,
  getFlags,
  updateEntryInstructionEmailPrefs,

};
